import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { formatDateShort } from "@/shared/lib/format-date";
import { i18n } from "@/shared/lib/i18n";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { useLedger } from "./use-ledger";
import { FinancePagination } from "./FinancePagination";
import { formatMoney, moneyFromCentsString } from "./money";
import { LEDGER_PAGE_SIZE } from "./constants";
import { LEDGER_ENTRY_TYPES, type LedgerEntry } from "./types";

const LABEL = "font-sans text-sm font-medium text-foreground";
const SELECT =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

export function LedgerPage() {
  const { t } = useTranslation();
  const [entryType, setEntryType] = useState<string | null>(null);
  const [venueId, setVenueId] = useState("");
  const [currency, setCurrency] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  // Debounce the free-text filters so typing doesn't fire a request per
  // keystroke (repo convention — same as venues/subscriptions/users lists).
  // entryType/from/to are discrete controls and don't need debouncing.
  const debouncedVenueId = useDebouncedValue(venueId, 300);
  const debouncedCurrency = useDebouncedValue(currency, 300);

  useEffect(() => {
    setPage(1);
  }, [entryType, debouncedVenueId, debouncedCurrency, from, to]);

  const { data, isLoading, isFetching, isError } = useLedger({
    entryType,
    venueId: debouncedVenueId,
    currency: debouncedCurrency,
    from,
    to,
    page,
  });
  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const hasFilters =
    entryType !== null ||
    venueId.trim() !== "" ||
    currency.trim() !== "" ||
    from !== "" ||
    to !== "";

  const handleReset = () => {
    setEntryType(null);
    setVenueId("");
    setCurrency("");
    setFrom("");
    setTo("");
  };

  return (
    <div data-testid="ledger-screen" className="space-y-6">
      <header className="space-y-2">
        <p className="brand-eyebrow">{t("finance.ledger.eyebrow")}</p>
        <h1 className="text-3xl">{t("finance.ledger.title")}</h1>
        <p className="font-sans text-base font-normal text-muted-foreground">
          {t("finance.ledger.subtitle")}
        </p>
      </header>

      <div data-testid="ledger-filters" className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <label htmlFor="ledger-type" className={LABEL}>
            {t("finance.ledger.filters.type")}
          </label>
          <select
            id="ledger-type"
            data-testid="filter-entry-type"
            value={entryType ?? ""}
            onChange={(e) => setEntryType(e.target.value === "" ? null : e.target.value)}
            className={SELECT}
          >
            <option value="">{t("finance.ledger.filters.typeAll")}</option>
            {LEDGER_ENTRY_TYPES.map((value) => (
              <option key={value} value={value}>
                {t(`finance.ledger.entryTypes.${value}`, { defaultValue: value })}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="ledger-venue" className={LABEL}>
            {t("finance.ledger.filters.venue")}
          </label>
          <Input
            id="ledger-venue"
            data-testid="filter-venue"
            value={venueId}
            onChange={(e) => setVenueId(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="ledger-currency" className={LABEL}>
            {t("finance.ledger.filters.currency")}
          </label>
          <Input
            id="ledger-currency"
            data-testid="filter-currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            maxLength={3}
            className="w-24"
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="ledger-from" className={LABEL}>
            {t("finance.ledger.filters.from")}
          </label>
          <Input
            id="ledger-from"
            data-testid="filter-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="ledger-to" className={LABEL}>
            {t("finance.ledger.filters.to")}
          </label>
          <Input
            id="ledger-to"
            data-testid="filter-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        {hasFilters && (
          <Button data-testid="ledger-filters-reset" variant="outline" onClick={handleReset}>
            {t("finance.ledger.filters.reset")}
          </Button>
        )}
      </div>

      {isLoading && (
        <p data-testid="ledger-loading" className="font-sans text-sm text-muted-foreground">
          {t("finance.ledger.loading")}
        </p>
      )}

      {isError && (
        <div
          data-testid="ledger-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t("finance.ledger.error")}
          </p>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div
          data-testid="ledger-empty"
          className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center"
        >
          <p className="font-sans text-sm font-medium text-foreground">
            {hasFilters ? t("finance.ledger.emptyFiltered") : t("finance.ledger.empty")}
          </p>
        </div>
      )}

      {!isError && items.length > 0 && (
        <div
          aria-busy={isFetching}
          className={isFetching ? "space-y-4 opacity-70 transition-opacity" : "space-y-4"}
        >
          <ul data-testid="ledger-list" className="space-y-2">
            {items.map((entry) => (
              <LedgerRow key={entry.id} entry={entry} />
            ))}
          </ul>
          <FinancePagination
            page={page}
            limit={LEDGER_PAGE_SIZE}
            totalCount={totalCount}
            onPageChange={setPage}
            testIdPrefix="ledger"
          />
        </div>
      )}
    </div>
  );
}

function LedgerRow({ entry }: { readonly entry: LedgerEntry }) {
  const { t } = useTranslation();
  const isOutflow = entry.amountCents.trim().startsWith("-");
  return (
    <li
      data-testid={`ledger-row-${entry.id}`}
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3"
    >
      <div className="min-w-0 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
            {/* Open-ended enum — unknown server types fall back to their raw code. */}
            {t(`finance.ledger.entryTypes.${entry.entryType}`, {
              defaultValue: entry.entryType,
            })}
          </span>
          <span className="font-sans text-xs text-muted-foreground">
            {formatDateShort(entry.occurredAt, i18n.language)}
          </span>
        </div>
        <p className="truncate font-sans text-sm text-foreground">{entry.description}</p>
      </div>
      <span
        data-testid={`ledger-amount-${entry.id}`}
        className={
          isOutflow
            ? "font-mono text-sm font-medium text-destructive"
            : "font-mono text-sm font-medium text-emerald-600"
        }
      >
        {formatMoney(moneyFromCentsString(entry.amountCents, entry.currency))}{" "}
        <span className="uppercase text-muted-foreground">{entry.currency}</span>
        <span className="sr-only">
          {isOutflow ? t("finance.ledger.outflow") : t("finance.ledger.inflow")}
        </span>
      </span>
    </li>
  );
}
